// Telegram startapp deep-link routing for landing previews.
// Grammar: startapp=<head>__src_<prefix>_<campaign>. Only the head drives routing
// here; the src segment is read server-side (auth/verify) for attribution.
// Spec: docs/superpowers/specs/2026-06-16-landing-preview-deeplinks-design.md

export const TOKEN_ROUTE: Record<string, string> = {
  brief: '/me/daily-brief',
  natal: '/me/natal-chart',
  natal_theme: '/me/natal-chart',
  natal_reading: '/me/natal-chart',
  check: '/check',
  lucky: '/lucky',
  when: '/when',
  ask: '/ask',
  course: '/course',
  zodiac_collection: '/flow/avatar/zodiac_collection',
  avatar_mini: '/flow/avatar/avatar_mini',
  past_lives_pack: '/flow/past-lives/past_lives_pack',
  couple_portrait: '/flow/couple',
  compat_check: '/flow/compat',
  gifting_crush: '/flow/gift',
  // The catalog is hidden; old links and the bot's «Что ещё открыть» button open the main screen.
  catalog: '/home',
  charts: '/me/charts',
  // A new conversation with the AI astrologer about the own chart (same target as newChatHref).
  chat: '/chat?surface=natal&seedRef=natal:self',
  // `home` and any unrecognized head intentionally absent → null (no deep target).
};

/** First `__`-delimited segment of the start_param (the routing head). */
export function parseHead(startParam: string): string {
  return startParam.split('__')[0] ?? '';
}

/**
 * A free-course link: `course`, `course__src_<tag>` or `course_<tag>` (e.g. `course_ig_bio`).
 * The head must be exactly `course` or start with `course_`, so `coursework` is not one.
 */
export function isCourseStart(startParam: string): boolean {
  const head = parseHead(startParam);
  return head === 'course' || head.startsWith('course_');
}

/**
 * `coursefb_<B###>` (the «Оставить отзыв» button in the bot's lesson finale): the lesson page scrolled to its
 * feedback form. Not a course start: it goes through the normal feature-link flow (isCourseStart stays false).
 */
export const FEEDBACK_LINK = /^coursefb_(B\d{3})$/;

/** Route for a recognized feature token, or null (home / unknown / legacy compat). */
export function routeForToken(startParam: string): string | null {
  if (!startParam) return null;
  if (isCourseStart(startParam)) return '/course';
  const feedback = FEEDBACK_LINK.exec(parseHead(startParam));
  if (feedback) return `/course/lesson/${feedback[1]}#feedback`;
  // thread_<id> opens an existing chat conversation (id: letters, digits, _ and -).
  const thread = /^thread_([A-Za-z0-9_-]{1,64})$/.exec(parseHead(startParam));
  if (thread) return '/chat?thread=' + thread[1];
  return TOKEN_ROUTE[parseHead(startParam)] ?? null;
}

const PENDING_KEY = 'pendingDeepLink';
const COURSE_START_KEY = 'courseStartHandled';

/**
 * A course link is followed once per app session: leaving the course goes to `/`, and RootRedirect must then
 * start the normal onboarding instead of sending the user back to the course.
 */
export function takeCourseStart(): boolean {
  try {
    const tg = window.Telegram?.WebApp as { initDataUnsafe?: { auth_date?: unknown; start_param?: unknown } } | undefined;
    const launch = String(tg?.initDataUnsafe?.auth_date ?? '') + (tg?.initDataUnsafe?.start_param ? ':' + String(tg.initDataUnsafe.start_param) : '');
    const done = sessionStorage.getItem(COURSE_START_KEY);
    if (done) {
      if (!launch || done === launch || done === '1') return false;
    }
    sessionStorage.setItem(COURSE_START_KEY, launch || '1');
    return true;
  } catch { return false; }
}

export function stashPendingDeepLink(route: string): void {
  try { sessionStorage.setItem(PENDING_KEY, route); } catch { /* sessionStorage unavailable */ }
}

export function takePendingDeepLink(): string | null {
  try {
    const v = sessionStorage.getItem(PENDING_KEY);
    if (v) sessionStorage.removeItem(PENDING_KEY);
    return v;
  } catch { return null; }
}
